import React, { useState, useEffect } from 'react';
import { X, Loader2, Check } from 'lucide-react';

const PERSONA_ICONS = {
  it_leadership: { emoji: null, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  healthcare_it: { emoji: null, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  unemployed_it: { emoji: null, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  founders: { emoji: null, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
};

const PersonaSelector = ({ currentPersonaId, onComplete, onSkip, isModal = true }) => {
  const [personas, setPersonas] = useState([]);
  const [selected, setSelected] = useState(currentPersonaId || null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchPersonas = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/auth/personas`);
        if (resp.ok) {
          const data = await resp.json();
          setPersonas(data.personas || []);
        }
      } catch (err) {
        console.error('Failed to fetch personas:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPersonas();
  }, [API_URL]);

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_URL}/api/auth/persona`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ persona_id: selected }),
      });
      if (resp.ok) {
        onComplete(selected);
      }
    } catch (err) {
      console.error('Failed to save persona:', err);
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <div data-testid="persona-selector">
      <div className="mb-5">
        <h2 className="text-xl font-semibold text-white mb-2">
          Choose your companion focus
        </h2>
        <p className="text-zinc-400 text-sm leading-relaxed">
          This shapes how your companion thinks and responds. You can change this anytime in Settings.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {personas.map((p) => {
            const style = PERSONA_ICONS[p.persona_id] || PERSONA_ICONS.it_leadership;
            const isSelected = selected === p.persona_id;
            return (
              <button
                key={p.persona_id}
                onClick={() => setSelected(p.persona_id)}
                className={`w-full text-left p-4 rounded-xl border transition-all ${
                  isSelected
                    ? `${style.bg} ${style.border} border-2`
                    : 'bg-zinc-800/50 border-zinc-700/50 hover:border-zinc-600'
                }`}
                data-testid={`persona-option-${p.persona_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? `${style.border} ${style.bg}` : 'border-zinc-600'
                  }`}>
                    {isSelected && <Check className={`w-3 h-3 ${style.color}`} />}
                  </div>
                  <div>
                    <p className={`font-medium text-sm ${isSelected ? style.color : 'text-zinc-200'}`}>
                      {p.display_name}
                    </p>
                    <p className="text-zinc-500 text-xs mt-1 leading-relaxed">
                      {p.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={!selected || saving}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg text-sm transition-colors disabled:opacity-50"
          data-testid="persona-save-button"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {saving ? 'Saving...' : 'Continue'}
        </button>
        {onSkip && (
          <button
            onClick={onSkip}
            className="px-4 py-2.5 text-zinc-500 hover:text-zinc-300 text-sm transition-colors"
            data-testid="persona-skip-button"
          >
            Decide later
          </button>
        )}
      </div>
    </div>
  );

  if (!isModal) return content;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="persona-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto">
        {onSkip && (
          <button
            onClick={onSkip}
            className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {content}
      </div>
    </div>
  );
};

export default PersonaSelector;
